import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import { 
  ArrowRight, 
  Clock, 
  User, 
  DollarSign, 
  Lightbulb, 
  MessageSquare, 
  ThumbsUp,
  Zap,
  Trophy,
  Target,
  CheckCircle,
  ChevronRight,
  BookOpen,
  TrendingUp,
  Users,
  BarChart3,
  Hourglass,
  Bot,
  Shield,
  Rocket,
  HeartHandshake,
  GraduationCap,
  AlertTriangle,
  Brain,
  Star,
  Quote,
  Gift,
  Calculator,
  Scale,
  Gem,
  Handshake,
  PiggyBank,
  BadgePercent,
  Crown,
  Coins,
  CircleDollarSign,
  HandCoins,
  TrendingDown
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const ObjecoesPreco = () => {
  const navigate = useNavigate();

  const priceObjectionTypes = [
    {
      number: "01",
      title: "A Objeção Genuína de Orçamento",
      subtitle: "Quando o cliente realmente não tem recursos disponíveis",
      description: "Esta é talvez a objeção mais desafiadora porque é baseada em uma limitação real. O cliente quer comprar, vê valor no seu produto ou serviço, mas genuinamente não possui os recursos financeiros no momento. Identificar essa objeção exige sensibilidade e as perguntas certas. Sinais incluem: pedidos específicos de parcelamento, comparações com outros gastos essenciais que estão sendo cortados, linguagem corporal de frustração genuína (não de negociação).",
      strategy: "A chave aqui não é forçar a venda, mas construir relacionamento para o futuro. Ofereça opções criativas: versões mais básicas do produto, períodos de teste, pagamentos escalonados. Se nada funcionar, mantenha contato. Um cliente que você tratou com respeito quando ele não podia comprar será seu maior defensor quando a situação financeira melhorar.",
      example: "\"Entendo perfeitamente, João. A situação econômica tem sido desafiadora para todos. Me conta: se pudéssemos encontrar uma forma de você começar com uma versão mais enxuta, pagando 40% menos, e depois expandir quando fizer sentido para o seu negócio, isso mudaria a conversa?\"",
      icon: PiggyBank
    },
    {
      number: "02",
      title: "A Objeção de Comparação",
      subtitle: "Quando existe um concorrente mais barato",
      description: "O cliente coloca seu preço lado a lado com o da concorrência e questiona a diferença. Esta é uma das objeções mais comuns e, ironicamente, uma das mais fáceis de resolver quando você conhece bem seu produto e o mercado. O erro fatal aqui é entrar em guerra de preços - você nunca vencerá essa batalha sem sacrificar suas margens.",
      strategy: "Primeiro, valide a objeção. Depois, mude o foco de preço para valor. Pergunte: 'Além do preço, o que mais você está comparando?' Então demonstre o que está incluso no seu preço que não está no concorrente: suporte, garantias, funcionalidades, qualidade. Faça o cliente calcular o custo total de propriedade, não apenas o preço de compra.",
      example: "\"É uma boa observação. O concorrente X realmente tem um preço inicial mais baixo. Posso te fazer uma pergunta? Você já conversou com clientes deles sobre o suporte pós-venda? Porque o que descobrimos é que nossos clientes economizam em média 30% a mais a longo prazo por não precisarem de consultoria externa, ter atualizações inclusas e um time de sucesso dedicado.\"",
      icon: Scale
    },
    {
      number: "03",
      title: "A Objeção de Teste",
      subtitle: "O cliente quer negociar - clássico jogo de negociação",
      description: "Muitos compradores são treinados para sempre pedir desconto. Não significa que não vão comprar pelo preço cheio, apenas que seria 'burrice' não tentar. Essa objeção é identificável por timing: geralmente aparece muito cedo na conversa (antes mesmo de entender totalmente a proposta) ou muito tarde (após tudo acordado, como última cartada).",
      strategy: "A pior resposta é dar o desconto imediatamente - isso sinaliza que seu preço era inflado. A melhor resposta é mostrar flexibilidade sem ceder no preço: 'Não consigo mexer no valor, mas posso adicionar três meses extras de suporte' ou 'O preço é esse, mas posso antecipar a entrega'. Você mantém sua margem e o cliente sente que 'ganhou' algo.",
      example: "\"Olha, eu adoraria poder baixar o preço para você, mas isso significaria entregar menos valor - e não é isso que queremos para sua empresa, certo? O que posso fazer é incluir o treinamento avançado da equipe, que normalmente cobramos separado. Isso te dá mais valor pelo mesmo investimento. Faz sentido?\"",
      icon: Handshake
    },
    {
      number: "04",
      title: "A Objeção de Percepção de Valor",
      subtitle: "O cliente não entende por que deveria pagar tanto",
      description: "Esta é a objeção mais importante de todas porque revela uma falha no seu processo de vendas. Se o cliente chega ao momento de discutir preço sem entender completamente o valor do que você oferece, o problema não é o preço - é a apresentação. A boa notícia é que essa objeção é 100% solucionável com comunicação.",
      strategy: "Volte dois passos. Faça perguntas para entender qual benefício mais importa para esse cliente específico. Depois, quantifique o valor em termos que ele entende. Se você vende software que economiza 10 horas por semana, calcule: 10 horas x R$50/hora x 52 semanas = R$26.000/ano. Seu software custa R$5.000? É um ROI de 520%.",
      example: "\"Deixa eu entender melhor: quando você olha para esse investimento, qual benefício você gostaria que ficasse mais claro? Porque se a questão é economia de tempo, posso te mostrar como um cliente semelhante ao seu perfil recuperou o investimento em 47 dias. Se é redução de erros, tenho outro case que talvez faça mais sentido.\"",
      icon: Gem
    },
    {
      number: "05",
      title: "A Objeção de Autoridade",
      subtitle: "O cliente precisa justificar o investimento para outros",
      description: "Vendedores experientes sabem que muitas vezes a pessoa com quem você está negociando quer comprar, mas não tem autonomia para aprovar o valor. Ela precisa 'vender internamente' para um chefe, um conselho, um sócio. Se você não der as ferramentas certas, ela vai perder essa batalha interna.",
      strategy: "Seu trabalho é transformar seu contato em um vendedor interno. Forneça: materiais que possam ser repassados (propostas claras, cases de sucesso, ROI calculado), argumentos contra objeções que ele vai ouvir, e disponibilidade para participar de reuniões com decisores. Facilite ao máximo a aprovação.",
      example: "\"Faz total sentido você precisar validar com a diretoria. Me conta: quais são as principais preocupações que eles costumam ter com investimentos dessa natureza? Posso preparar um material específico endereçando esses pontos, e se ajudar, posso participar de uma call rápida com eles para tirar dúvidas técnicas. O que seria mais útil?\"",
      icon: Crown
    }
  ];

  const psychologyPrinciples = [
    {
      title: "Ancoragem",
      description: "O primeiro número mencionado em uma negociação serve como âncora psicológica. Se você apresentar o valor completo do que seu produto entrega (em economia, ganhos, benefícios) ANTES de revelar o preço, o cérebro do cliente já estará ancorado em um número maior. Seu preço parecerá pequeno em comparação.",
      tip: "Sempre apresente o valor antes do preço. 'Nossos clientes economizam em média R$50.000 por ano. O investimento para conseguir isso é de R$5.000 anuais.'",
      icon: Target
    },
    {
      title: "Aversão à Perda",
      description: "Estudos de behavioral economics mostram que o medo de perder é aproximadamente duas vezes mais poderoso que o desejo de ganhar. Isso significa que mostrar o que o cliente PERDE por não comprar é mais eficaz do que mostrar o que ele ganha comprando.",
      tip: "Quantifique o custo da inação. 'Cada mês sem nossa solução representa R$4.000 em eficiência perdida. Em um ano, são R$48.000 que você deixou na mesa.'",
      icon: TrendingDown
    },
    {
      title: "Prova Social",
      description: "Quando estamos incertos sobre uma decisão, olhamos para o que outros semelhantes a nós fizeram. Testemunhos, cases de sucesso e números de clientes satisfeitos reduzem a percepção de risco e justificam o preço.",
      tip: "Use cases de empresas similares. 'A empresa X, que é do seu segmento e tamanho, teve exatamente essa mesma preocupação. Depois de 6 meses, o CFO deles me disse que foi o melhor investimento do ano.'",
      icon: Users
    },
    {
      title: "Escassez",
      description: "O que é raro é valioso. Isso não significa criar falsas urgências (isso destrói confiança), mas sim comunicar limitações reais: capacidade de atendimento, vagas em treinamentos, condições especiais temporárias.",
      tip: "Seja honesto sobre limitações reais. 'Nosso time de implementação consegue atender apenas 3 novos clientes por mês para garantir a qualidade. Temos uma vaga para o próximo mês.'",
      icon: Hourglass
    },
    {
      title: "Reciprocidade",
      description: "Quando você dá algo de valor, a pessoa sente uma obrigação psicológica de retribuir. Por isso consultores que entregam insights valiosos durante o processo de vendas têm taxas de conversão muito maiores.",
      tip: "Entregue valor antes de pedir a venda. Uma análise gratuita, um diagnóstico, um conselho que já ajuda mesmo antes da compra cria boa vontade que facilita a negociação.",
      icon: Gift
    },
    {
      title: "Consistência",
      description: "Pessoas querem ser consistentes com seus compromissos anteriores. Se durante a conversa o cliente concordou que precisa resolver um problema, que tempo é dinheiro, que qualidade importa - ele terá dificuldade de recusar baseado apenas em preço.",
      tip: "Obtenha micro-compromissos ao longo da conversa. 'Você concorda que resolver esse gargalo deveria ser prioridade esse trimestre?' Depois é difícil dizer que o preço é alto demais para uma prioridade.",
      icon: CheckCircle
    }
  ];

  const practicalScripts = [
    {
      situation: "Cliente diz: 'Está caro demais'",
      badResponse: "Posso fazer um desconto de 20%",
      goodResponse: "Entendo. Caro comparado a quê, especificamente? Ao que você paga hoje ou a outra solução que está avaliando?",
      explanation: "A resposta ruim imediatamente cede valor. A resposta boa busca entender a base de comparação para poder endereçar a objeção real."
    },
    {
      situation: "Cliente diz: 'Vou pensar'",
      badResponse: "Ok, fico no aguardo do seu retorno",
      goodResponse: "Claro, faz sentido. Para eu poder te ajudar a pensar: qual é a principal dúvida que você ainda tem? Geralmente é sobre implementação, resultados ou o investimento em si?",
      explanation: "'Vou pensar' quase sempre mascara uma objeção não verbalizada. A resposta boa tenta trazer à tona para poder resolver."
    },
    {
      situation: "Cliente diz: 'O concorrente X custa metade'",
      badResponse: "Mas somos melhores que eles",
      goodResponse: "É uma diferença significativa mesmo. Me conta: no seu levantamento, você conseguiu comparar o que está incluído em cada proposta? Porque frequentemente a diferença de preço reflete diferenças importantes em escopo.",
      explanation: "A resposta ruim é defensiva e vaga. A resposta boa convida o cliente a fazer uma análise mais profunda, geralmente revelando que a comparação não é justa."
    },
    {
      situation: "Cliente diz: 'Meu orçamento é R$X (muito abaixo)'",
      badResponse: "Infelizmente não conseguimos atender nesse valor",
      goodResponse: "Obrigado por compartilhar. Esse orçamento cobre qual parte do problema que você quer resolver? Talvez possamos estruturar em fases, começando pelo que é mais urgente.",
      explanation: "A resposta ruim encerra a conversa. A resposta boa abre possibilidades de solução criativa que atenda ambos os lados."
    },
    {
      situation: "Cliente diz: 'Preciso de desconto para aprovar'",
      badResponse: "Posso fazer 15% nessa condição",
      goodResponse: "Se conseguirmos encontrar uma forma de tornar isso possível, você estaria pronto para fechar hoje? E me conta: além do valor, existe algum outro ponto que precisaria ser ajustado na proposta?",
      explanation: "A resposta ruim dá desconto sem garantias. A resposta boa primeiro confirma o compromisso e verifica se há outras objeções escondidas."
    }
  ];

  const commonMistakes = [
    {
      mistake: "Dar desconto no primeiro pedido",
      consequence: "Sinaliza que seu preço era inflado, ensina o cliente a sempre pedir desconto, reduz suas margens desnecessariamente",
      solution: "Sempre peça algo em troca: pagamento antecipado, contrato mais longo, indicações. Nunca dê desconto 'porque pediu'."
    },
    {
      mistake: "Falar de preço cedo demais",
      consequence: "O cliente não tem contexto de valor para avaliar o preço, a conversa vira sobre números antes de sobre benefícios",
      solution: "Desenvolva completamente o problema, a solução e o valor antes de revelar preço. Quando perguntar antes da hora: 'Depende de alguns fatores. Posso fazer algumas perguntas para te dar um número preciso?'"
    },
    {
      mistake: "Defender o preço com justificativas de custo",
      consequence: "Cliente não liga para quanto custa para você produzir, liga para quanto vale para ele",
      solution: "Nunca diga 'esse é o custo porque...'. Sempre fale de valor: 'esse investimento representa X em retorno porque...'"
    },
    {
      mistake: "Assumir que objeção de preço é definitiva",
      consequence: "Você desiste de vendas que poderiam ser fechadas, perde receita e relacionamentos",
      solution: "Trate objeção de preço como pedido de mais informação. O cliente está dizendo 'me convença', não 'não quero'."
    },
    {
      mistake: "Competir apenas em preço",
      consequence: "Corrida para o fundo, margens cada vez menores, empresa insustentável",
      solution: "Diferencie-se em valor: atendimento, garantias, personalização, expertise. Deixe guerra de preço para commodities."
    }
  ];

  const advancedTechniques = [
    {
      name: "Técnica do Sanduíche de Valor",
      description: "Estruture sua apresentação como um sanduíche: comece com benefícios tangíveis, apresente o preço no meio, e termine com mais benefícios e garantias. O preço fica 'protegido' entre camadas de valor.",
      steps: [
        "Apresente 3-4 benefícios principais com números",
        "Revele o preço de forma confiante",
        "Imediatamente adicione bônus, garantias e suporte incluso",
        "Termine com case de sucesso ou depoimento"
      ]
    },
    {
      name: "Técnica da Decomposição",
      description: "Quando o valor total parece alto, decomponha em unidades menores que façam mais sentido. R$12.000 por ano parece muito; R$32 por dia (menos que um almoço executivo) parece razoável.",
      steps: [
        "Identifique uma unidade de medida relevante (dia, por usuário, por transação)",
        "Faça a divisão mantendo a precisão",
        "Compare com gastos cotidianos similares",
        "Contraste o custo diário com o valor diário gerado"
      ]
    },
    {
      name: "Técnica do Custo do Não-Fazer",
      description: "Mude o frame da discussão. Em vez de 'quanto custa comprar', discuta 'quanto custa não comprar'. Quantifique perdas, ineficiências, oportunidades perdidas, riscos de manter o status quo.",
      steps: [
        "Identifique as dores atuais do cliente",
        "Quantifique cada dor em valores monetários",
        "Projete para 12 meses",
        "Compare: 'Você vai gastar R$50.000 de qualquer forma - na ineficiência ou na solução. A diferença é que um traz retorno, o outro não.'"
      ]
    },
    {
      name: "Técnica da Garantia Reversa",
      description: "Se você confia no seu produto, ofereça uma garantia que elimina o risco do cliente. 'Se você não obtiver X resultado em Y tempo, devolvemos seu dinheiro' é poderoso porque demonstra confiança e remove a objeção de 'e se não funcionar?'",
      steps: [
        "Defina um resultado mensurável e realista",
        "Estabeleça um prazo justo para esse resultado",
        "Seja específico sobre as condições",
        "Apresente com confiança, não como desespero"
      ]
    },
    {
      name: "Técnica do Teste de Compromisso",
      description: "Antes de negociar preço, teste o nível de compromisso do cliente. Se ele não está comprometido, desconto não vai fechar a venda - apenas reduzir sua margem em uma venda que não aconteceria de qualquer forma.",
      steps: [
        "Pergunte: 'Se resolvermos a questão do preço, há algo mais que precisaríamos alinhar?'",
        "Ouça atentamente a resposta",
        "Só negocie se a resposta for 'não, só o preço'",
        "Se houver outras objeções, resolva-as primeiro"
      ]
    }
  ];

  const industrySpecificApproaches = [
    {
      industry: "SaaS e Tecnologia",
      approach: "Foque em custo total de propriedade (TCO), não no preço da licença. Inclua: custos de implementação concorrente, treinamento, manutenção, atualizações, integrações. Seu produto 'caro' frequentemente é mais barato no total.",
      metric: "ROI em 12-24 meses, tempo para valor, custo por usuário produtivo"
    },
    {
      industry: "Serviços Profissionais",
      approach: "Venda expertise, não horas. O cliente não está pagando por 10 horas de consultoria, está pagando por 15 anos de experiência condensados em uma solução. Precifique por projeto e resultado, não por hora.",
      metric: "Valor do problema resolvido, custo de erro, aceleração de resultados"
    },
    {
      industry: "Vendas B2B Enterprise",
      approach: "Em vendas complexas, o preço raramente é decidido por uma pessoa. Entenda o processo de compra, mapeie stakeholders, e forneça argumentos customizados para cada perfil: técnico, financeiro, executivo.",
      metric: "Business case completo, análise de risco, alinhamento estratégico"
    },
    {
      industry: "Vendas para PMEs",
      approach: "Donos de PMEs misturam dinheiro pessoal e empresarial emocionalmente. Reconheça que 'caro' pode significar 'arriscado'. Reduza percepção de risco com garantias, casos de empresas similares, implementação gradual.",
      metric: "Payback em meses, comparativo com custos atuais, flexibilidade de pagamento"
    }
  ];

  const statistics = [
    { value: "44%", label: "dos vendedores desistem após a primeira objeção de preço", icon: AlertTriangle },
    { value: "80%", label: "das vendas são fechadas entre a 5ª e 12ª interação", icon: TrendingUp },
    { value: "35%", label: "dos clientes que dizem 'caro' compram com o argumento certo", icon: CircleDollarSign },
    { value: "2x", label: "mais eficaz é falar de perda do que de ganho em negociação", icon: Brain }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Objeções de Preço: Como Transformar em Oportunidade de Venda",
    "description": "Guia completo sobre como lidar com objeções de preço em vendas. Aprenda técnicas avançadas, scripts prontos e a psicologia por trás das negociações para converter mais clientes.",
    "author": {
      "@type": "Person",
      "name": "Junior Bianchim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vendas Trainer"
    },
    "datePublished": "2025-10-20",
    "dateModified": "2025-10-20",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/objecoes-preco"
    }
  };

  return (
    <>
      <SEOHead 
        title="Objeções de Preço: Como Transformar em Oportunidade de Venda"
        description="Guia completo sobre como lidar com objeções de preço em vendas. Aprenda técnicas avançadas, scripts prontos e a psicologia por trás das negociações para converter mais clientes."
        keywords="objeções de preço, como lidar com objeções, negociação de vendas, vendas B2B, técnicas de fechamento, converter clientes, superar objeções"
        canonicalUrl="https://bianchimbusiness.com.br/posts/objecoes-preco"
        ogType="article"
        articlePublishedTime="2024-12-15T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          <article>
            {/* Hero Section */}
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{ 
                  backgroundImage: "url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80')" 
                }}
                role="img"
                aria-label="Imagem de fundo representando negociação e preços"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
              
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm">
                      <DollarSign className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm font-medium text-primary">Negociação</span>
                    </span>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Objeções de Preço:
                    </span>
                    <br />
                    <span className="text-foreground">Como Transformar em Oportunidade</span>
                  </h1>
                  
                  <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    Descubra como os melhores vendedores do mundo transformam "está caro demais" 
                    em contratos assinados. Técnicas comprovadas, scripts prontos e psicologia de negociação.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2025-10-20">20 Out 2025</time>
                      <span>• 15 min de leitura</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Statistics Section */}
            <section className="py-12 sm:py-16" aria-labelledby="stats-heading">
              <div className="container mx-auto px-4">
                <h2 id="stats-heading" className="sr-only">Estatísticas sobre objeções de preço</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {statistics.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Card 
                        key={index}
                        className="p-4 sm:p-6 text-center border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all"
                      >
                        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary mx-auto mb-3" aria-hidden="true" />
                        <p className="text-2xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Introduction Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
                  <div className="flex-1 order-2 lg:order-1">
                    <h2 id="intro-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">
                      Por Que "Está Caro" É a Melhor Objeção Que Você Pode Ouvir
                    </h2>
                    <p className="text-muted-foreground mb-4 text-base sm:text-lg">
                      Quando um cliente diz que seu produto ou serviço está caro, a maioria dos vendedores 
                      entra em modo defensivo. O coração acelera, as mãos suam, e o instinto é justificar 
                      ou, pior, oferecer um desconto imediatamente. <strong className="text-foreground">Esse é o 
                      maior erro que você pode cometer.</strong>
                    </p>
                    <p className="text-muted-foreground mb-4 text-base sm:text-lg">
                      Aqui está a verdade que vendedores de elite entendem: quando alguém diz "está caro", 
                      significa que eles estão <em>interessados</em>. Pense bem - ninguém perde tempo 
                      discutindo preço de algo que não quer. Se não houvesse interesse, a resposta seria 
                      "não tenho interesse" ou "não preciso disso". A objeção de preço é, na verdade, 
                      um convite para continuar a conversa.
                    </p>
                    <p className="text-muted-foreground mb-6 text-base sm:text-lg">
                      Este artigo é um guia completo para transformar essa objeção aparentemente negativa 
                      em oportunidades reais de fechamento. Você vai aprender a psicologia por trás da 
                      percepção de preço, técnicas comprovadas de resposta, scripts prontos para usar, 
                      e como treinar sua equipe para nunca mais perder uma venda por causa de "está caro".
                    </p>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-primary/20">
                      <Lightbulb className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Insight chave:</strong> Pesquisas mostram que 
                        35% dos clientes que inicialmente dizem "está caro" acabam comprando quando o 
                        vendedor responde corretamente. Você está deixando um terço das suas vendas na mesa.
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 order-1 lg:order-2">
                    <figure>
                      <img 
                        src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=700&q=80" 
                        alt="Profissional de vendas em negociação com cliente"
                        className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                        loading="lazy"
                      />
                      <figcaption className="text-center text-sm text-muted-foreground mt-3">
                        A objeção de preço é uma oportunidade disfarçada
                      </figcaption>
                    </figure>
                  </div>
                </div>
              </div>
            </section>

            {/* Types of Price Objections */}
            <section className="py-12 sm:py-20" aria-labelledby="types-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="types-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Os 5 Tipos de Objeção de Preço
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Nem toda objeção de preço é igual. Identificar o tipo correto é o primeiro passo 
                    para formular a resposta mais eficaz.
                  </p>
                </div>

                <div className="space-y-8 sm:space-y-16">
                  {priceObjectionTypes.map((type, index) => {
                    const Icon = type.icon;
                    const isEven = index % 2 === 0;
                    
                    return (
                      <div 
                        key={index}
                        className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-start`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                              <span className="text-xl sm:text-2xl font-bold text-white">{type.number}</span>
                            </div>
                            <div>
                              <h3 className="text-xl sm:text-2xl font-bold">{type.title}</h3>
                              <p className="text-primary text-sm sm:text-base">{type.subtitle}</p>
                            </div>
                          </div>
                          
                          <p className="text-muted-foreground mb-4 text-base sm:text-lg leading-relaxed">
                            {type.description}
                          </p>
                          
                          <div className="p-4 sm:p-6 rounded-xl bg-muted/50 border border-border mb-4">
                            <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4 text-primary" aria-hidden="true" />
                              Estratégia Recomendada
                            </h4>
                            <p className="text-muted-foreground text-sm sm:text-base">
                              {type.strategy}
                            </p>
                          </div>
                          
                          <div className="p-4 sm:p-6 rounded-xl bg-primary/5 border border-primary/20">
                            <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-primary" aria-hidden="true" />
                              Exemplo de Resposta
                            </h4>
                            <p className="text-muted-foreground italic text-sm sm:text-base">
                              "{type.example}"
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0 w-full lg:w-48">
                          <Card className="p-6 sm:p-8 text-center border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
                            <Icon className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-4" aria-hidden="true" />
                            <p className="text-xs sm:text-sm text-muted-foreground">Tipo {type.number}</p>
                          </Card>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Psychology Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="psychology-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="psychology-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      A Psicologia Por Trás do Preço
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Entender como o cérebro processa decisões de compra é essencial para 
                    responder objeções de forma eficaz.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {psychologyPrinciples.map((principle, index) => {
                    const Icon = principle.icon;
                    return (
                      <Card 
                        key={index}
                        className="p-6 sm:p-8 border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all"
                      >
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" aria-hidden="true" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-3">{principle.title}</h3>
                        <p className="text-muted-foreground text-sm sm:text-base mb-4 leading-relaxed">
                          {principle.description}
                        </p>
                        <div className="p-3 sm:p-4 rounded-lg bg-primary/5 border border-primary/20">
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            <strong className="text-foreground">Dica prática:</strong> {principle.tip}
                          </p>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Practical Scripts Section */}
            <section className="py-12 sm:py-20" aria-labelledby="scripts-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="scripts-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Scripts Prontos Para Usar
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Respostas testadas e aprovadas para as situações mais comuns de objeção de preço.
                  </p>
                </div>

                <div className="space-y-6 sm:space-y-8">
                  {practicalScripts.map((script, index) => (
                    <Card 
                      key={index}
                      className="p-6 sm:p-8 border-primary/10 bg-card/50 backdrop-blur-sm"
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                          <span className="text-lg sm:text-xl font-bold text-white">{index + 1}</span>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold">{script.situation}</h3>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                        <div className="p-4 sm:p-5 rounded-xl bg-destructive/10 border border-destructive/20">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-destructive" aria-hidden="true" />
                            <span className="text-sm font-semibold text-destructive">Resposta Ruim</span>
                          </div>
                          <p className="text-muted-foreground text-sm sm:text-base italic">"{script.badResponse}"</p>
                        </div>
                        
                        <div className="p-4 sm:p-5 rounded-xl bg-primary/10 border border-primary/20">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-primary">Resposta Eficaz</span>
                          </div>
                          <p className="text-muted-foreground text-sm sm:text-base italic">"{script.goodResponse}"</p>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-muted/50 border border-border">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          <strong className="text-foreground">Por que funciona:</strong> {script.explanation}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 sm:py-20">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 border-primary/30 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 animate-pulse" />
                  <div className="relative z-10">
                    <Bot className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-6" aria-hidden="true" />
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                      <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                        Pratique Essas Técnicas Com IA
                      </span>
                    </h2>
                    <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      Ler sobre técnicas é importante, mas praticar é o que transforma conhecimento em habilidade. 
                      No Vendas Trainer, você pode simular centenas de cenários de objeção de preço com feedback 
                      instantâneo.
                    </p>
                    <Button 
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-glow text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                    >
                      Começar a Praticar Grátis
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </Button>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-4">
                      ✓ 10 simulações grátis &nbsp; ✓ Feedback instantâneo &nbsp; ✓ Sem cartão de crédito
                    </p>
                  </div>
                </Card>
              </div>
            </section>

            {/* Common Mistakes Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="mistakes-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="mistakes-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-destructive to-destructive/70 bg-clip-text text-transparent">
                      5 Erros Fatais em Negociação de Preço
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Evite esses erros comuns que custam milhares de reais em vendas perdidas.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {commonMistakes.slice(0, 3).map((item, index) => (
                    <Card 
                      key={index}
                      className="p-6 border-destructive/20 bg-card/50 backdrop-blur-sm hover:border-destructive/40 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden="true" />
                        </div>
                        <h3 className="font-bold text-destructive/90">{item.mistake}</h3>
                      </div>
                      <p className="text-muted-foreground text-sm mb-4">
                        <strong className="text-foreground">Consequência:</strong> {item.consequence}
                      </p>
                      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          <strong className="text-primary">✓ Solução:</strong> {item.solution}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mt-6 sm:mt-8">
                  {commonMistakes.slice(3).map((item, index) => (
                    <Card 
                      key={index}
                      className="p-6 border-destructive/20 bg-card/50 backdrop-blur-sm hover:border-destructive/40 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-destructive" aria-hidden="true" />
                        </div>
                        <h3 className="font-bold text-destructive/90">{item.mistake}</h3>
                      </div>
                      <p className="text-muted-foreground text-sm mb-4">
                        <strong className="text-foreground">Consequência:</strong> {item.consequence}
                      </p>
                      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          <strong className="text-primary">✓ Solução:</strong> {item.solution}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Advanced Techniques Section */}
            <section className="py-12 sm:py-20" aria-labelledby="advanced-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="advanced-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      5 Técnicas Avançadas de Negociação
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Estratégias utilizadas por vendedores de alto desempenho para fechar negócios complexos.
                  </p>
                </div>

                <div className="space-y-6 sm:space-y-8">
                  {advancedTechniques.map((technique, index) => (
                    <Card 
                      key={index}
                      className="p-6 sm:p-8 border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all"
                    >
                      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold">{technique.name}</h3>
                          </div>
                          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                            {technique.description}
                          </p>
                        </div>
                        
                        <div className="lg:w-80 flex-shrink-0">
                          <div className="p-4 sm:p-5 rounded-xl bg-muted/50 border border-border">
                            <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                              <Zap className="w-4 h-4 text-primary" aria-hidden="true" />
                              Passo a Passo
                            </h4>
                            <ol className="space-y-2">
                              {technique.steps.map((step, stepIndex) => (
                                <li key={stepIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                                    {stepIndex + 1}
                                  </span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Industry Specific Section */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="industry-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 id="industry-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Abordagens Por Segmento
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                    Cada indústria tem suas particularidades. Adapte sua estratégia ao seu mercado.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                  {industrySpecificApproaches.map((item, index) => (
                    <Card 
                      key={index}
                      className="p-6 sm:p-8 border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all"
                    >
                      <h3 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" aria-hidden="true" />
                        {item.industry}
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base mb-4 leading-relaxed">
                        {item.approach}
                      </p>
                      <div className="p-3 sm:p-4 rounded-lg bg-primary/5 border border-primary/20">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          <strong className="text-foreground">📊 Métricas-chave:</strong> {item.metric}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Summary Section */}
            <section className="py-12 sm:py-20" aria-labelledby="summary-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="summary-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 text-center">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Resumo: O Framework Completo
                    </span>
                  </h2>
                  
                  <div className="prose prose-lg max-w-none">
                    <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6">
                      Lidar com objeções de preço não é sobre ter a resposta perfeita decorada. É sobre 
                      entender profundamente o que está por trás da objeção e responder de forma que 
                      crie valor, construa confiança e guie o cliente para a decisão certa.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 my-8">
                      <Card className="p-5 sm:p-6 border-primary/20">
                        <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                          <Brain className="w-5 h-5 text-primary" aria-hidden="true" />
                          Mentalidade Correta
                        </h3>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                            Objeção de preço = interesse genuíno
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                            Seu trabalho é esclarecer valor, não defender preço
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                            Nunca dê desconto sem receber algo em troca
                          </li>
                        </ul>
                      </Card>
                      
                      <Card className="p-5 sm:p-6 border-primary/20">
                        <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                          <Target className="w-5 h-5 text-primary" aria-hidden="true" />
                          Processo Em 4 Passos
                        </h3>
                        <ol className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                            Identifique o tipo de objeção
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                            Faça perguntas para entender a raiz
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                            Reframe de preço para valor
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                            Teste o compromisso e feche
                          </li>
                        </ol>
                      </Card>
                    </div>
                    
                    <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                      A prática é o que separa vendedores medianos de vendedores excepcionais. Leia este 
                      artigo novamente, anote os scripts que mais fazem sentido para seu negócio, e 
                      pratique diariamente. Se possível, use ferramentas como o Vendas Trainer para 
                      simular cenários e receber feedback objetivo.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Author Section */}
            <section className="py-12 sm:py-16 bg-muted/30" aria-labelledby="author-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-card/50 backdrop-blur-sm">
                    <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                      <img 
                        src={juniorBianchim}
                        alt="Foto de Junior Bianchim, autor do artigo e especialista em vendas"
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-primary/20 flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="text-center sm:text-left">
                        <h3 id="author-heading" className="text-lg sm:text-xl font-bold mb-2">Junior Bianchim</h3>
                        <p className="text-primary text-sm mb-3">Especialista em Vendas e Fundador do Bianchim Business</p>
                        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                          Com mais de 10 anos de experiência em vendas B2B, incluindo passagens pela 
                          Sênior Sistemas e outras grandes empresas brasileiras. Hoje é professor de 
                          Vendas, Comércio e Administração no SENAC e dedica-se a ajudar vendedores 
                          a alcançarem seu potencial máximo através de tecnologia e metodologias 
                          comprovadas.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-12 sm:py-20" aria-labelledby="related-heading">
              <div className="container mx-auto px-4">
                <h2 id="related-heading" className="text-2xl sm:text-3xl font-bold mb-8 text-center">
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Continue Aprendendo
                  </span>
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  <Link to="/posts/7-tecnicas-fechamento" className="block group">
                    <Card className="p-6 h-full border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                          Técnicas de Vendas
                        </span>
                        <span className="text-xs text-muted-foreground">8 min</span>
                      </div>
                      <h3 className="text-lg font-bold group-hover:text-primary transition-colors mb-2">
                        7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        Aprenda as estratégias de fechamento mais eficazes para converter prospects em clientes.
                      </p>
                    </Card>
                  </Link>
                  
                  <Link to="/posts/ia-treinamento-vendas" className="block group">
                    <Card className="p-6 h-full border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-1 rounded-full">
                          Tecnologia
                        </span>
                        <span className="text-xs text-muted-foreground">12 min</span>
                      </div>
                      <h3 className="text-lg font-bold group-hover:text-primary transition-colors mb-2">
                        Como a IA Está Revolucionando o Treinamento de Vendas
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        Entenda como a inteligência artificial pode acelerar o desenvolvimento da sua equipe.
                      </p>
                    </Card>
                  </Link>
                </div>
              </div>
            </section>

            {/* Final CTA */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="final-cta-heading">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/20 via-accent/20 to-secondary/20 border-primary/30 text-center relative overflow-hidden max-w-4xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 animate-pulse" />
                  <div className="relative z-10">
                    <h2 id="final-cta-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                      <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                        Domine Objeções Com Prática Real
                      </span>
                    </h2>
                    <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      A teoria você já tem. Agora é hora de praticar. Com o Vendas Trainer, você 
                      simula centenas de negociações com IA e recebe feedback instantâneo para 
                      aprimorar suas técnicas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button 
                        size="lg"
                        onClick={() => navigate("/auth")}
                        className="bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-glow text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                      >
                        Começar Simulações Grátis
                        <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                      </Button>
                      <Button 
                        size="lg"
                        variant="outline"
                        onClick={() => navigate("/")}
                        className="border-primary/50 hover:bg-primary/10 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                      >
                        Conhecer o Vendas Trainer
                      </Button>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-6">
                      ✓ 10 simulações grátis &nbsp; ✓ Cenários de objeção de preço &nbsp; ✓ Feedback instantâneo
                    </p>
                  </div>
                </Card>
              </div>
            </section>
          </article>
        </main>

        <PageNavigation />
        <Footer />
      </div>
    </>
  );
};

export default ObjecoesPreco;
